import { useState } from "react";
import {
  Button,
  DataState,
  LeaderboardFilterChips,
  PageHeader,
  Table,
  type Column,
} from "@vms/ui-web";
import type { Leaderboard } from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";
import { LeaderboardDetailModal } from "../../components/leaderboards/LeaderboardDetailModal";

/**
 * Per-venue leaderboards list. Hub-authored — read-only on the cloud (sync
 * authority matrix is hub-master for the `leaderboard` entity). The table
 * mirrors hub-ui's LeaderboardManagementList layout so operators see the
 * same Name / Filters / Branding shape across both UIs. Editing redirects
 * to the hub via the detail modal's footer.
 */
export function VenueLeaderboards(): JSX.Element | null {
  const { currentVenue } = useOrgContext();
  const leaderboards = useQuery<Leaderboard[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/leaderboards` : null,
  );
  const [detailTarget, setDetailTarget] = useState<Leaderboard | null>(null);

  if (!currentVenue) return null;
  const rows = leaderboards.data ?? [];

  const columns: Column<Leaderboard>[] = [
    {
      key: "name",
      header: "Name",
      render: (lb) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">{lb.name}</div>
          {lb.subheading && (
            <div className="text-xs text-gray-500 truncate">{lb.subheading}</div>
          )}
        </div>
      ),
    },
    {
      key: "filters",
      header: "Filters",
      render: (lb) => (
        <LeaderboardFilterChips
          game={lb.game}
          eventIds={lb.eventIds}
          tracks={lb.tracks}
          cars={lb.cars}
          dateFrom={lb.dateFrom}
          dateTo={lb.dateTo}
        />
      ),
    },
    {
      key: "branding",
      header: "Branding",
      render: (lb) =>
        lb.brandingTemplateId ? (
          <span className="text-xs text-gray-600">Custom</span>
        ) : (
          <span className="text-xs text-gray-400">Default</span>
        ),
    },
    {
      key: "updated",
      header: "Updated",
      render: (lb) => (
        <span className="text-xs text-gray-500">
          {new Date(lb.updatedAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (lb) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={(ev) => {
              ev.stopPropagation();
              setDetailTarget(lb);
            }}
          >
            View details
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={(ev) => {
              ev.stopPropagation();
              window.open(`/display/leaderboard/${lb.id}`, "_blank", "noopener");
            }}
          >
            Open display
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Leaderboards"
        subtitle="Saved leaderboards for this venue. Editing happens on the hub UI; click 'View details' to inspect filters or 'Open display' to project on a venue TV."
      />
      <DataState
        loading={leaderboards.loading}
        error={leaderboards.error}
        empty={!leaderboards.loading && rows.length === 0}
        emptyMessage="No saved leaderboards yet."
      >
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <Table<Leaderboard>
            columns={columns}
            data={rows}
            keyExtractor={(lb) => lb.id}
            onRowClick={(lb) => setDetailTarget(lb)}
          />
        </div>
      </DataState>

      {detailTarget && (
        <LeaderboardDetailModal
          isOpen
          onClose={() => setDetailTarget(null)}
          leaderboard={detailTarget}
          venueId={currentVenue.id}
        />
      )}
    </div>
  );
}
